import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Reminder,
  ReminderEventType,
  NotificationChannel,
  Vehicle,
} from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Plus,
  Bell,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Repeat,
} from 'lucide-react';
import { ReminderForm } from './ReminderForm';
import { formatDate } from '../../utils/dateFormat';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const eventTypeLabels: Record<ReminderEventType, string> = {
  oil_change: 'Заміна масла',
  inspection: 'Техогляд',
  insurance: 'Страхування',
  tire_change: 'Заміна шин',
  custom: 'Кастомне',
};

const channelLabels: Record<NotificationChannel, string> = {
  email: 'Email',
  telegram: 'Telegram',
};

export const Reminders = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [filterVehicle, setFilterVehicle] = useState<string>('');

  const { data: vehicles } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      if (!user) return [];
      const token = await user.getIdToken();
      const response = await axios.get(`${API_URL}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data || [];
    },
    enabled: !!user,
  });

  const { data: reminders, isLoading } = useQuery({
    queryKey: ['reminders', filterVehicle],
    queryFn: async () => {
      if (!user) return [];
      const token = await user.getIdToken();
      const url = filterVehicle
        ? `${API_URL}/reminders/vehicle/${filterVehicle}`
        : `${API_URL}/reminders`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (response.data.data || []) as Reminder[];
      return data.map((reminder: Reminder) => ({
        ...reminder,
        nextTriggerDate: reminder.nextTriggerDate || undefined,
      }));
    },
    enabled: !!user,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      await axios.delete(`${API_URL}/reminders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      toast.success(t('reminder.deleteSuccess'));
    },
    onError: (error: unknown) => {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error
          : undefined;
      toast.error(errorMessage || t('errors.generic'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      await axios.patch(
        `${API_URL}/reminders/${id}/toggle`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
    },
    onError: (error: unknown) => {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error
          : undefined;
      toast.error(errorMessage || t('errors.generic'));
    },
  });

  const handleAdd = () => {
    setEditingReminder(null);
    setIsFormOpen(true);
  };

  const handleEdit = (reminder: Reminder) => {
    setEditingReminder(reminder);
    setIsFormOpen(true);
  };

  const handleClose = () => {
    setIsFormOpen(false);
    setEditingReminder(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['reminders'] });
    handleClose();
  };

  const getVehicleName = (vehicleId: string) => {
    const vehicle = vehicles?.find((v: Vehicle) => v.id === vehicleId);
    return vehicle
      ? `${vehicle.name} (${vehicle.make} ${vehicle.model})`
      : vehicleId;
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{t('reminder.title')}</h1>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          {t('reminder.add')}
        </button>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('common.filter')} {t('nav.vehicles')}
        </label>
        <select
          value={filterVehicle}
          onChange={(e) => setFilterVehicle(e.target.value)}
          className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">
            {t('common.all')} {t('nav.vehicles')}
          </option>
          {vehicles?.map((vehicle: Vehicle) => (
            <option key={vehicle.id} value={vehicle.id}>
              {vehicle.name} ({vehicle.make} {vehicle.model})
            </option>
          ))}
        </select>
      </div>

      {reminders && reminders.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('reminder.noReminders')}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {reminders?.map((reminder: Reminder) => (
            <div
              key={reminder.id}
              className={`bg-white rounded-lg shadow p-6 border-l-4 ${
                reminder.isActive
                  ? 'border-primary-500'
                  : 'border-gray-300 opacity-60'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{reminder.title}</h3>
                    <span className="px-2 py-1 text-xs bg-primary-100 text-primary-800 rounded">
                      {eventTypeLabels[reminder.eventType]}
                    </span>
                    {reminder.type === 'recurring' && (
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <Repeat className="w-4 h-4" />
                        {t('reminder.types.recurring')}
                      </span>
                    )}
                  </div>
                  {reminder.description && (
                    <p className="text-gray-600 mb-3">{reminder.description}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {reminder.type === 'recurring'
                          ? `${t('reminder.interval')}: ${reminder.interval} ${
                              reminder.intervalUnit === 'days'
                                ? t('reminder.intervalUnits.days')
                                : reminder.intervalUnit === 'weeks'
                                  ? t('reminder.intervalUnits.weeks')
                                  : t('reminder.intervalUnits.months')
                            }`
                          : formatDate(reminder.triggerValue)}
                      </span>
                    </div>
                    <div>
                      {t('nav.vehicles')}: {getVehicleName(reminder.vehicleId)}
                    </div>
                    <div>
                      {t('reminder.channels')}:{' '}
                      {reminder.notificationChannels
                        .map((ch: NotificationChannel) => channelLabels[ch])
                        .join(', ')}
                    </div>
                    {(reminder.nextTriggerDate ||
                      reminder.type === 'recurring') && (
                      <div>
                        {t('reminder.nextTrigger')}:{' '}
                        {reminder.nextTriggerDate
                          ? formatDate(reminder.nextTriggerDate)
                          : formatDate(reminder.triggerValue)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleMutation.mutate(reminder.id)}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    title={
                      reminder.isActive
                        ? t('reminder.deactivate')
                        : t('reminder.activate')
                    }
                  >
                    {reminder.isActive ? (
                      <ToggleRight className="w-6 h-6" />
                    ) : (
                      <ToggleLeft className="w-6 h-6" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(reminder)}
                    className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                    title={t('common.edit')}
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t('reminder.deleteConfirm'))) {
                        deleteMutation.mutate(reminder.id);
                      }
                    }}
                    className="p-2 text-red-600 hover:text-red-700 transition-colors"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <ReminderForm
          reminder={editingReminder}
          vehicles={vehicles || []}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};
